'use client';

import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { videoReplicationsApi, type KagemaneGifts, type VideoReplicationModel } from '@/lib/api/video-replications';

/** The server reserves the coupon; this read-only snapshot only offers the choice. */
export function KagemaneGiftChoice({ ownerId, selected, disabled, duration, model, videoModel, resolution,
  refreshKey, onChange, onAvailabilityChange, onStatusChange,
}: {
  ownerId: string;
  selected: boolean;
  disabled: boolean;
  duration: number | undefined;
  model: 'seedance' | 'heygen' | 'kling4' | 'genjutsu';
  videoModel: VideoReplicationModel;
  resolution: string;
  refreshKey: number;
  onChange: (selected: boolean) => void;
  onAvailabilityChange: (available: boolean) => void;
  onStatusChange?: (status: 'loading' | 'error' | 'ready' | 'unavailable') => void;
}) {
  const t = useTranslations('kagemane');
  const locale = useLocale();
  const [gifts, setGifts] = useState<KagemaneGifts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setGifts(null);
    async function load() {
      try {
        const response = await videoReplicationsApi.gifts();
        if (cancelled) return;
        if (!response.success || !response.data) throw new Error('gift_snapshot_unavailable');
        setGifts(response.data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [ownerId, refreshKey, retry]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') setRetry(value => value + 1); };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [ownerId]);
  const durationFits = typeof duration === 'number' && Number.isFinite(duration)
    && duration >= 4 && duration <= (gifts?.max_source_duration_sec ?? gifts?.max_duration_sec ?? 0);
  const contractMatches = (gifts?.model === 'selected' || gifts?.model === 'seedance-2.0') && gifts.resolution === '1080p'
    && gifts.max_duration_sec === 15;
  const hasGift = !loading && !error && contractMatches && (gifts?.monthly_gifts_remaining ?? 0) > 0;
  const canUse = hasGift && durationFits && (gifts?.model === 'selected'
    ? Boolean(gifts.models?.includes(videoModel)) && (gifts.resolutions ?? ['1080p']).includes(resolution)
    : model === 'seedance' && resolution === gifts?.resolution);
  useEffect(() => { onAvailabilityChange(canUse); }, [canUse, model, videoModel, resolution, duration, onAvailabilityChange]);
  const status = loading ? 'loading' : error ? 'error' : canUse ? 'ready' : 'unavailable';
  useEffect(() => { onStatusChange?.(status); }, [status, onStatusChange]);

  if (!loading && !error && !selected && (!gifts || gifts.monthly_gift_limit === 0)) return null;
  return <div className="rounded-2xl border border-[#D5EBF5] bg-[#F1FAFE] px-4 py-3 text-sm">
    <p className="font-medium">{t('giftTitle')}</p>
    {loading ? <p role="status" className="mt-1 text-xs text-[#53636D]">{t('giftLoading')}</p>
      : error ? <div role="alert" className="mt-1 text-xs text-[#53636D]">
        <p>{t('giftLoadError')}</p>
        <button type="button" disabled={disabled} onClick={() => setRetry(value => value + 1)}
          className="mt-1 min-h-9 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]">{t('giftRetry')}</button>
      </div> : <p aria-live="polite" className="mt-1 text-xs text-[#53636D]">
        <span>{t('giftRemaining', { remaining: gifts?.monthly_gifts_remaining ?? 0, total: gifts?.monthly_gift_limit ?? 0 })}</span>
        {gifts?.gift_resets_at && <span className="block">{t('giftResets', { date: new Date(gifts.gift_resets_at).toLocaleDateString(locale) })}</span>}
      </p>}
    {!loading && !error && <button type="button" disabled={disabled} onClick={() => setRetry(value => value + 1)}
      className="mt-1 min-h-9 text-xs underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]">{t('giftRetry')}</button>}
    <label className="mt-2 flex min-h-11 items-center gap-2">
      <input type="checkbox" checked={selected} onChange={event => onChange(event.target.checked)}
        disabled={disabled || (!selected && (!hasGift || !durationFits || !canUse))}
        className="size-4 shrink-0 accent-[#4ABDE9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]" />
      <span>{t('giftUse')}</span>
    </label>
    <p className="text-xs leading-5 text-[#53636D]">{t('giftRules')}</p>
    {selected && !canUse && !loading && <p role="alert" className="mt-2 text-xs leading-5 text-[#8A421B]">
      {hasGift ? t('giftMismatch') : t('giftUnavailable')}
    </p>}
  </div>;
}
